import type { ReactElement, ReactNode } from "react";
import type { HostHealth, LibraryView, VolumeView } from "../../lib/status";
import { serviceUrl } from "../../lib/links";
import { useCountUp } from "../../lib/motion";
import { TopbarSettings } from "../TopbarSettings";

/* One stroke vocabulary: 1.8, round caps/joins, currentColor, fill none. */
export const SI = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export function Chevron() {
  return (
    <svg {...SI} className="go" width="15" height="15" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

/*
 * The tile is decoration, not status. Every row that carries a tile must
 * still carry its semantic status indicator — the corner badge on the
 * tile, not the tile's own gradient. Identity is derived from id / role
 * / label so a newly added service falls back to a grey well and a
 * two-letter monogram without this list being edited.
 */
const SVC_TILES: { ids: string[]; match: RegExp; key: string; mono: string }[] = [
  { ids: ["playback", "plex"], match: /\bplex\b/i, key: "plex", mono: "PX" },
  { ids: ["tv-uhd", "sonarr-4k"], match: /sonarr.*4k|tv-uhd|sonarr-4k/i, key: "sonarr-4k", mono: "4K" },
  { ids: ["movies-uhd", "radarr-4k"], match: /radarr.*4k|movies-uhd|radarr-4k/i, key: "radarr-4k", mono: "4K" },
  { ids: ["tv", "sonarr"], match: /\bsonarr\b/i, key: "sonarr", mono: "SO" },
  { ids: ["movies", "radarr"], match: /\bradarr\b/i, key: "radarr", mono: "RA" },
  { ids: ["downloads", "sabnzbd", "sab"], match: /\bsabnzbd\b|\bsab\b/i, key: "sabnzbd", mono: "SA" },
  { ids: ["prowlarr"], match: /\bprowlarr\b/i, key: "prowlarr", mono: "PR" },
  { ids: ["bazarr"], match: /\bbazarr\b/i, key: "bazarr", mono: "BZ" },
  { ids: ["overseerr"], match: /\boverseerr\b/i, key: "overseerr", mono: "OV" },
  { ids: ["homebridge"], match: /\bhomebridge\b/i, key: "homebridge", mono: "HB" },
  { ids: ["npm"], match: /\bnpm\b|nginx.?proxy/i, key: "npm", mono: "NP" },
];

export function serviceIdentity(
  id: string,
  label: string,
  role?: string,
): { key: string; mono: string } {
  if (role === "playback") return { key: "plex", mono: "PX" };
  const hay = `${id} ${label}`;
  for (const t of SVC_TILES) {
    if (t.ids.includes(id) || t.match.test(hay)) return { key: t.key, mono: t.mono };
  }
  if (role === "download") return { key: "sabnzbd", mono: "SA" };
  const letters = label.replace(/[^A-Za-z0-9]/g, "");
  return { key: "unknown", mono: (letters.slice(0, 2) || "??").toUpperCase() };
}

export function ServiceTile({
  id,
  label,
  role,
  ok,
}: {
  id: string;
  label: string;
  role?: string;
  ok: boolean;
}) {
  const { key, mono } = serviceIdentity(id, label, role);
  return (
    <span className={`svc-tile svc-tile--${key}`} aria-hidden="true">
      {mono}
      <span className={`svc-tile__badge ${ok ? "dot--ok dot--live" : "dot--fault"}`} />
    </span>
  );
}

export function ExternalMark() {
  return (
    <svg {...SI} className="go" width="15" height="15" aria-hidden="true">
      <path d="M14 4h6v6M20 4l-8 8" />
      <path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
    </svg>
  );
}

/**
 * A row that goes somewhere.
 *
 * `href` opens the real thing in its own tab (Sonarr, Plex, SABnzbd); `to`
 * navigates inside the console. Anything with neither stays an inert div
 * rather than pretending to be clickable — a button that does nothing is
 * worse than no button.
 */
export function Row({
  href,
  to,
  title,
  className = "",
  children,
}: {
  href?: string;
  to?: () => void;
  title?: string;
  className?: string;
  children: ReactNode;
}) {
  const cls = `section__row ${className}`;
  if (href) {
    return (
      <a className={`${cls} section__row--go`} href={href} target="_blank" rel="noreferrer" title={title}>
        {children}
        <ExternalMark />
      </a>
    );
  }
  if (to) {
    return (
      <button type="button" className={`${cls} section__row--go`} onClick={to} title={title}>
        {children}
        <Chevron />
      </button>
    );
  }
  return <div className={cls}>{children}</div>;
}

/** One KPI tile. Its number animates between polls so a change is visible. */
export function Stat({
  label,
  icon,
  value,
  text,
  unit,
  level,
  onClick,
  hint,
  live,
  severityValue,
}: {
  label: string;
  icon: ReactElement;
  value?: number;
  text?: string;
  unit?: string;
  level?: "ok" | "warn" | "fault";
  onClick?: () => void;
  hint?: string;
  /** Gradient icon well — only while this metric is actually moving. */
  live?: boolean;
  /** Colour the number by severity — only when the number itself is the severity (Faults). */
  severityValue?: boolean;
}) {
  const shown = useCountUp(value ?? 0);
  const showPill = Boolean(level && level !== "ok" && !severityValue);
  const body = (
    <>
      <span className="stat__top">
        <span className={`stat__icon${live ? " stat__icon--live" : ""}`} aria-hidden="true">
          {icon}
        </span>
        {showPill && <span className={`pill pill--${level}`}>{level}</span>}
      </span>
      <span className="stat__value">
        {text ?? shown}
        {unit && <span className="stat__unit">{unit}</span>}
      </span>
      <span className="stat__label">{label}</span>
      {hint && <span className="stat__hint">{hint}</span>}
    </>
  );
  const cls = `stat${level ? ` stat--${level}` : ""}${severityValue ? " stat--severity-value" : ""}`;
  return onClick ? (
    <button type="button" className={`${cls} stat--go`} onClick={onClick}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function Section({
  id,
  title,
  aside,
  flashing,
  children,
}: {
  id: string;
  title: string;
  aside?: ReactNode;
  flashing: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className={`card section${flashing === id ? " is-flash" : ""}`}>
      <div className="section__head">
        <h2>{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Meter({ v, onAsk }: { v: VolumeView; onAsk: () => void }) {
  const pct = v.level === "unknown" ? "—" : `${v.usedPct}%`;
  const freeLine =
    v.level === "unknown"
      ? "not configured"
      : v.ok === false
        ? "unreachable"
        : `${v.freeText} free of ${v.totalText}`;
  return (
    <div className="vol">
      <div className="vol__head">
        <div className="vol__label">{v.label}</div>
        <div className={`vol__pct vol__free--${v.level}`}>{pct}</div>
      </div>
      <div className={`meter meter--${v.level}`} role="img" aria-label={`${v.usedPct}% used`}>
        <i style={{ transform: `scaleX(${Math.min(100, Math.max(2, v.usedPct)) / 100})` }} />
      </div>
      <div className="vol__foot">
        <span>{freeLine}</span>
        <button type="button" className="linkish" onClick={onAsk}>
          Ask →
        </button>
      </div>
    </div>
  );
}

export function hostDot(ok: boolean | null): string {
  return ok === null ? "dot" : ok ? "dot dot--ok dot--live" : "dot dot--fault";
}

export function HostRow({ h, onAsk }: { h: HostHealth; onAsk: () => void }) {
  return (
    <div className="section__row">
      <span className={hostDot(h.ok)} />
      <div className="grow">
        <strong>{h.name}</strong>
        <div className="section__sub mono truncate">{h.detail}</div>
      </div>
      <button type="button" className="linkish" onClick={onAsk}>
        Ask →
      </button>
    </div>
  );
}

export function LibraryRow({ l }: { l: LibraryView }) {
  return (
    <Row href={serviceUrl(l.url)} title={`Open ${l.label}`}>
      <ServiceTile id={l.id} label={l.label} ok={l.ok} />
      <div className="grow">
        <div className="row">
          <strong>{l.label}</strong>
          {!l.ok && <span className="pill pill--fault">{l.detail}</span>}
        </div>
        <div className="section__sub">
          {l.ok
            ? `${l.count.toLocaleString()} items · ${l.sizeText}${l.missing ? ` · ${l.missing} missing` : ""}`
            : "no data while this instance is down"}
        </div>
      </div>
      {l.queued > 0 && <span className="pill pill--grad">{l.queued}</span>}
    </Row>
  );
}

export function bytesLabel(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return "—";
  const tb = bytes / 1024 ** 4;
  if (tb >= 1) return `${tb.toFixed(tb >= 10 ? 1 : 2)} TB`;
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(gb >= 100 ? 0 : 1)} GB`;
  return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
}

export function PageSkeleton({ title, stats = 3 }: { title: string; stats?: number }) {
  return (
    <div className="page">
      <header className="topbar">
        <h1 className="topbar__title">{title}</h1>
        <div className="topbar__right">
          <TopbarSettings />
        </div>
      </header>
      <div className="page-body">
        <div className="page-inner stack">
          <div className={`stats${stats === 4 ? " stats--4" : ""}`}>
            {Array.from({ length: stats }, (_, i) => (
              <div key={i} className="skel skel--stat" />
            ))}
          </div>
          <div className="skel skel--card" />
          <div className="skel skel--card" />
        </div>
      </div>
    </div>
  );
}
